import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Text, useResizeObserver, useTranslation, useUi } from '@weareconceptstudio/core';

//* Components
import Tag from '@/components/Tag';
import Image from '@/components/Image';
import Editor from '@/components/Editor';
import Share from '@/components/Share';

//* Style
import MainSectionStyle from './style';

gsap.registerPlugin(ScrollTrigger);

const MainSection = ({ title, highlight, description, date, read_time, industries, banner_image, gallery }) => {
	const { translate } = useTranslation();
	const { winWidth } = useUi();
	const [dimensions, setDimensions] = useState({
		headerHeight: 0,
	});
	const { headerHeight } = dimensions;
	const resizes = useResizeObserver(typeof window !== 'undefined' ? document.body : null);

	//! Refs
	const shareWrapperRef = useRef();
	const triggerRef = useRef();
	const descRef = useRef();

	//! Calculate element sizes
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const getElementSize = (selector, prop = 'clientHeight') => document.querySelector(selector)?.[prop] ?? 0;

		setDimensions({
			headerHeight: getElementSize('header'),
		});
	}, [resizes, winWidth]);

	//! Pin share
	useEffect(() => {
		if (shareWrapperRef.current && descRef.current) {
			const endTrigger = document.querySelector('.description-wrap');
			const endMarker = shareWrapperRef.current.offsetHeight;
			const headerPadding = headerHeight;

			shareWrapperRef.current.style.cssText = `padding-top: ${headerPadding}px; margin-top: -${headerPadding}px;`;

			if (endTrigger) {
				triggerRef.current = ScrollTrigger.create({
					start: `top top`,
					end: `bottom top+=${endMarker + headerPadding + 'px'}`,
					trigger: shareWrapperRef.current,
					// markers: true,
					pin: true,
					pinSpacing: false,
					endTrigger: endTrigger,
				});
			}
		}

		return () => {
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
		};
	}, [winWidth]);

	return (
		<MainSectionStyle>
			<Container
				full
				isSection
				className={'row'}
			>
				<div className={'col-12'}>
					<div className={'date-wrapper'}>
						<Text className={'p1 montserrat-400 black-800'}>{date}</Text>
						<div className={'dot'} />
						<Text className={'p1 montserrat-400 black-800'}>
							{read_time} {translate('min_read')}
						</Text>
					</div>
				</div>

				<div className={'col-10 col-dx-12 col-t-9'}>
					<Text
						tag={'h1'}
						className={'h2 montserrat-400 title'}
					>
						{title}
					</Text>
				</div>

				<div className={'col-2 col-dx-12 col-t-3'} />

				<div className={'col-12'}>
					<div className={'industries-wrapper'}>
						{industries?.map((industry, idx) => (
							<Tag key={idx}>{industry}</Tag>
						))}
					</div>
				</div>

				<div className={'col-12'}>
					<div className='banner-image-wrapper'>
						<Image
							src={banner_image.src}
							alt={banner_image.alt}
						/>
					</div>
				</div>

				<div
					className={'col-11 col-dx-12 description-wrap'}
					ref={descRef}
				>
					{highlight && <Text className={'h6 montserrat-400 black-900 highlight'}>{highlight}</Text>}

					{description && (
						<Editor
							data={description}
							className={'desc'}
						/>
					)}
				</div>

				<div className={'col-1 col-dx-12'}>
					<div className='share-pin-wrapper'>
						<div
							ref={shareWrapperRef}
							className='share-inner-wrapper'
						>
							<Share />
						</div>
					</div>
				</div>
			</Container>
		</MainSectionStyle>
	);
};

export default MainSection;
